import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,

  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, EmojiEvents as EmojiEventsIcon, Close as CloseIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { assignAward, getAvailableAwards, getStudentAwards, updateAwardAssignment, deleteAwardAssignment } from '../../services/awardService';

const StudentAwardsTab = ({ studentId, teacherId, studentName, managedStudentId = null, onClose, onUpdated = null, debug = false }) => {
  console.log('[StudentAwardsTab] Rendered with studentId:', studentId, 'studentName:', studentName);
  
  const [awards, setAwards] = useState([]);
  const [availableAwards, setAvailableAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openAwardDialog, setOpenAwardDialog] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [formData, setFormData] = useState({
    award: '',
    notes: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Helper to convert private object storage URLs to proxy URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('/api/exams/media-proxy/')) return url;
    if (url.includes('objectstorage') && url.includes('oraclecloud.com')) {
      try {
        const u = new URL(url);
        const parts = u.pathname.split('/');
        const oIdx = parts.indexOf('o');
        if (oIdx !== -1 && parts.length > oIdx + 1) {
          const objectName = parts.slice(oIdx + 1).join('/');
          return `/api/exams/media-proxy/0/image/?object_name=${encodeURIComponent(objectName)}`;
        }
      } catch(e) { console.error('parse oci url', e); }
    }
    return url.startsWith('http') ? url : (url.startsWith('/') ? url : `/${url}`);
  };
  const isMounted = useRef(true);

  // Fetch awards for the exact student and teacher IDs provided
  const fetchAwards = async () => {
    if (!studentId) {
      setAwards([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use the awardService instead of direct fetch
      const awardsResponse = await getStudentAwards(studentId, teacherId);
      const list = Array.isArray(awardsResponse.data) ? awardsResponse.data : [];
      setAwards(list);

      const availResp = await getAvailableAwards().catch(() => []);
      const avail = Array.isArray(availResp) ? availResp : (availResp?.data || []);
      setAvailableAwards(avail);
    } catch (err) {
      console.error('[fetchAwards] error:', err);
      setSnackbar({ open: true, message: 'Failed to fetch awards', severity: 'error' });
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    console.log('[useEffect] StudentId changed or component mounted');
    isMounted.current = true;
    
    if (studentId) {
      console.log('[useEffect] Fetching awards for studentId:', studentId);
      fetchAwards();
    } else {
      console.log('[useEffect] No studentId, not fetching');
      if (isMounted.current) setLoading(false);
    }
    
    return () => {
      console.log('[useEffect] Cleanup - unmounting');
      isMounted.current = false;
    };
  }, [studentId]);

  const handleOpenAwardDialog = (award = null) => {
    if (award) {
      console.log('Editing award:', award);
      setEditingAward(award.id);
      setFormData({
        award: award.award_details?.id || award.award,
        notes: award.notes || ''
      });
    } else {
      setEditingAward(null);
      setFormData({
        award: "",
        notes: ""
      });
    }
    setOpenAwardDialog(true);
  };

  const handleCloseAwardDialog = () => {
    setOpenAwardDialog(false);
    setEditingAward(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submissionData = {
      student: String(studentId),
      award: String(formData.award),
      notes: formData.notes
    };
    if (managedStudentId) {
      submissionData.teacher_managed_student = String(managedStudentId);
    }
    console.log('[handleSubmit] Submitting award assignment with data:', submissionData);
    
    try {
      const response = await assignAward(submissionData);
      console.log('[handleSubmit] Award assignment successful:', response);
      
      // Close the dialog and reset form
      handleCloseAwardDialog();
      
      // Explicitly reload awards after successful submission
      console.log('[handleSubmit] Reloading awards after successful submission...');
      fetchAwards();
      if (typeof onUpdated === 'function') onUpdated();
      setSnackbar({ open: true, message: 'Award assigned successfully!', severity: 'success' });
    } catch (error) {
      console.error('[handleSubmit] Error assigning award:', error);
      
      // Check for validation errors from the server
      if (error.response && error.response.data) {
        const errorMessages = [];
        Object.entries(error.response.data).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach(error => errorMessages.push(`${field}: ${error}`));
          } else if (typeof errors === 'string') {
            errorMessages.push(`${field}: ${errors}`);
          }
        });
        
        if (errorMessages.length > 0) {
          setSnackbar({
            open: true,
            message: `Validation error: ${errorMessages.join(', ')}`,
            severity: 'error'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Error assigning award. Please try again.',
            severity: 'error'
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: 'Error assigning award. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleDeleteAward = async (awardId) => {
    if (window.confirm('Are you sure you want to delete this award assignment?')) {
      try {
        await deleteAwardAssignment(awardId);
        setSnackbar({
          open: true,
          message: 'Award assignment deleted successfully!',
          severity: 'success'
        });
        fetchAwards();
        if (typeof onUpdated === 'function') onUpdated();
      } catch (error) {
        console.error('Error deleting award:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete award. Please try again.',
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Dialog 
      open={true} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {studentName}'s Awards
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAwardDialog()}
                sx={{ mb: 2 }}
              >
                Give Award
              </Button>
            </Box>

            
            {!Array.isArray(awards) || awards.length === 0 ? (
              <Box textAlign="center" py={4}>
                <EmojiEventsIcon 
                  color="disabled" 
                  sx={{ fontSize: 60, opacity: 0.5, mb: 2 }} 
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No Awards Yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  This student hasn't received any awards yet.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {awards.map((award) => (
                  <Grid item xs={12} sm={6} md={4} key={award.id}>
                    <Card variant="outlined">
                      <CardHeader
                        title={award.award_details?.category || award.award?.category || 'Award'}
                        subheader={`Awarded on ${format(new Date(award.created_at || new Date()), 'MMM d, yyyy')}`}
                        action={
                          <>
                            
                            <Tooltip title="Delete">
                              <IconButton 
                                onClick={() => handleDeleteAward(award.id)} 
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        }
                      />
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                          {award.award_details?.image_url && (
                            <Box 
                              component="img" 
                              src={getImageUrl(award.award_details.image_url)} 
                              alt={award.award_details.name || award.award_details?.category || 'Award'}
                              sx={{ width: 60, height: 60, objectFit: 'contain' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" align="center" sx={{ fontWeight: 500, color: 'text.primary' }}>
                          {(award.award_details?.name || award.award_details?.category || award.award?.category || 'Award').split(':')[0]}
                        </Typography>
                        {award.notes && (
                          <Typography variant="body2" color="text.secondary">
                            {award.notes}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>

      {/* Award Assignment Dialog */}
      <Dialog open={openAwardDialog} onClose={handleCloseAwardDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAward ? 'Edit Award' : 'Give Award'}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="award-label">Award</InputLabel>
              <Select
                labelId="award-label"
                id="award"
                name="award"
                value={formData.award}
                onChange={handleChange}
                required
              >
                <MenuItem value="">Select an award</MenuItem>
                {availableAwards.map((award) => (
                  <MenuItem key={award.id} value={award.id}>
                    {award.category}
                    {award.description ? ` - ${award.description}` : ''}
                  </MenuItem>
                ))}
                {availableAwards.length === 0 && (
                  <MenuItem value="" disabled>
                    No awards available
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleCloseAwardDialog} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingAward ? 'Update' : 'Assign'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default StudentAwardsTab;
